// src/context/AuthContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api/authService';
import {
  hasSessionTiming,
  maybeRefreshSession,
  sessionRefreshConfig,
  clearSessionTiming,
} from '../services/api/sessionRefresh';
import { clearServiceWizardSessionStorage } from '../utils/wizardClientSession';
import {
  getNavigateToLoginOptions,
  registerSessionExpiredHandler,
  resolveReturnPathForLogin,
} from '../utils/authSessionExpired';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    return registerSessionExpiredHandler(async () => {
      if (!userRef.current) return false;

      clearServiceWizardSessionStorage();
      setUser(null);
      clearSessionTiming();

      try {
        await authService.logOut();
      } catch (error) {
        console.error('Logout error during session expiry:', error);
      }

      navigate(
        '/login',
        getNavigateToLoginOptions(resolveReturnPathForLogin()),
      );
      return true;
    });
  }, [navigate]);

  useEffect(() => {
    if (!user) return undefined;

    const syncSession = () => {
      void maybeRefreshSession();
    };

    const intervalId = window.setInterval(
      syncSession,
      sessionRefreshConfig.pollIntervalMs,
    );

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncSession();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    syncSession();

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user]);

  const login = async (email, password) => {
    try {
      await authService.login(email, password);
      const fetchedUser = await authService.fetchUser();
      if (!fetchedUser) throw new Error('Failed to fetch user.');
      setUser(fetchedUser);
      return { success: true, userData: fetchedUser };
    } catch (error) {
      clearSessionTiming();
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await authService.logOut();
    } catch (error) {
      console.error('Logout error:', error);
      clearSessionTiming();
    } finally {
      clearServiceWizardSessionStorage();
      navigate('/', { replace: true });
      setUser(null);
    }
  };

  /** Loads the current account using the session cookie (e.g. after provider enroll). */
  const refreshUser = async () => {
    try {
      const fetchedUser = await authService.fetchUser();
      if (!fetchedUser) {
        return { success: false, error: 'No session' };
      }
      setUser(fetchedUser);
      if (!hasSessionTiming()) {
        try {
          await authService.refreshSession();
        } catch (error) {
          console.error('Session sync after refreshUser failed:', error);
        }
      }
      return { success: true, userData: fetchedUser };
    } catch (error) {
      console.error('refreshUser error:', error);
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const fetchedUser = await authService.fetchUser();
        if (!fetchedUser) return;

        setUser(fetchedUser);
        try {
          await authService.refreshSession();
        } catch (error) {
          console.error('Initial session sync failed:', error);
        }
      } catch (err) {
        console.error('Failed to auto-load user:', err);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
